/**
 * antd theme for the whole portal.
 *
 * Mirrors the design tokens in `index.css` (`--rt-*`). antd computes derived
 * colours at runtime from plain values, so the hexes are repeated here rather
 * than referenced as CSS variables — keep the two in sync when either changes.
 *
 * The intent: soft layered surfaces, hairline borders instead of hard boxes,
 * generous radii and calm hover states — the same language as the active exam
 * session screen.
 */

const surface = "#272727";
const surfaceRaised = "#2d2d2d";
const elevated = "#232323";
const line = "rgba(255,255,255,0.08)";
const lineStrong = "rgba(255,255,255,0.2)";
const accent = "#EC171E";

const theme = {
  token: {
    colorPrimary: "#9B1015",
    fontFamily: "Roboto, system-ui, -apple-system, Segoe UI, sans-serif",

    borderRadius: 10,
    borderRadiusLG: 16,
    borderRadiusSM: 8,
    borderRadiusXS: 6,

    controlHeight: 38,
    controlHeightLG: 46,
    controlHeightSM: 30,

    colorBgContainer: surface,
    colorBgElevated: elevated,
    colorBorder: line,
    colorBorderSecondary: line,
    colorSplit: line,

    colorText: "#ffffff",
    colorTextSecondary: "rgba(255,255,255,0.72)",
    colorTextTertiary: "rgba(255,255,255,0.55)",
    colorTextQuaternary: "rgba(255,255,255,0.35)",
    colorTextPlaceholder: "rgba(255,255,255,0.4)",

    colorSuccess: "#4CAF82",
    colorWarning: "#ECBF17",
    colorError: "#EC171E",

    boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
    boxShadowSecondary: "0 12px 32px rgba(0,0,0,0.28)",
  },
  components: {
    Button: {
      primaryShadow: "none",
      defaultShadow: "none",
      colorTextDisabled: "rgba(255,255,255,0.4)",
      colorBgContainerDisabled: "rgba(255,255,255,0.08)",
      borderColorDisabled: "transparent",
      borderRadius: 10,
      borderRadiusLG: 12,
      borderRadiusSM: 8,
      defaultBorderColor: accent,
      defaultGhostColor: accent,
      defaultGhostBorderColor: accent,
      defaultColor: accent,
      defaultBg: "transparent",
      defaultHoverBg: "rgba(236,23,30,0.12)",
      defaultHoverBorderColor: accent,
      defaultHoverColor: "#ff5f64",
      fontWeight: 500,
      contentFontSize: 14,
    },

    Layout: {
      headerBg: "#232323",
      siderBg: "#232323",
      colorText: "#ffffff",
      headerColor: "#ffffff",
      fontSize: 18,
      bodyBg: "#1c1c1c",
      triggerBg: "#2d2d2d",
    },

    Table: {
      borderColor: line,
      headerColor: "rgba(255,255,255,0.6)",
      headerBg: "#232323",
      headerSplitColor: "transparent",
      headerBorderRadius: 14,
      borderRadius: 14,
      borderRadiusLG: 14,
      rowHoverBg: "rgba(255,255,255,0.06)",
      cellPaddingBlock: 14,
      colorBgContainer: "transparent",
      footerBg: "transparent",
      stickyScrollBarBg: "rgba(255,255,255,0.2)",
    },
    Empty: {
      colorTextDescription: "rgba(255, 255, 255, 0.4)",
    },
    Card: {
      colorBgContainer: surface,
      borderRadiusLG: 16,
      headerBg: "transparent",
      colorBorderSecondary: line,
      boxShadowTertiary: "0 6px 18px rgba(0,0,0,0.22)",
    },
    Modal: {
      colorBgContainer: elevated,
      contentBg: elevated,
      headerBg: elevated,
      footerBg: "transparent",
      borderRadiusLG: 18,
      boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
    },
    Input: {
      colorBorder: line,
      activeBorderColor: accent,
      hoverBorderColor: lineStrong,
      activeShadow: "0 0 0 3px rgba(236,23,30,0.15)",
      errorActiveShadow: "none",
      borderRadius: 10,
      colorBgContainer: surface,
      paddingInline: 14,
    },
    Select: {
      colorBorder: line,
      multipleItemBg: "rgba(255,255,255,0.1)",
      colorBgElevated: elevated,
      optionActiveBg: "rgba(255,255,255,0.07)",
      optionSelectedBg: "rgba(236,23,30,0.18)",
      borderRadius: 10,
      borderRadiusLG: 12,
      controlItemBgActive: "rgba(236,23,30,0.18)",
      controlItemBgHover: "rgba(255,255,255,0.07)",
      optionPadding: "8px 12px",
    },
    InputNumber: {
      colorBorder: line,
      activeShadow: "0 0 0 3px rgba(236,23,30,0.15)",
      activeBorderColor: accent,
      hoverBorderColor: lineStrong,
      borderRadius: 10,
      colorIcon: "#ffffff",
      colorIconHover: "#ffffff",
      errorActiveShadow: "none",
      colorBgContainer: surface,
    },

    Menu: {
      itemBg: "transparent",
      subMenuItemBg: "transparent",
      itemSelectedBg: "rgba(236,23,30,0.16)",
      itemSelectedColor: "#ffffff",
      itemHoverBg: "rgba(255,255,255,0.07)",
      itemHoverColor: "#ffffff",
      itemColor: "rgba(255,255,255,0.72)",
      itemBorderRadius: 10,
      itemMarginInline: 8,
      itemHeight: 44,
      iconSize: 20,
      collapsedIconSize: 20,
      iconMarginInlineEnd: 12,
      groupTitleColor: "rgba(255,255,255,0.35)",
      groupTitleFontSize: 11,
    },

    DatePicker: {
      colorTextDisabled: "#9D9D9D",
      colorBgElevated: elevated,
      colorBgContainer: surface,
      activeBg: surface,
      colorBorder: line,
      activeBorderColor: accent,
      hoverBorderColor: lineStrong,
      activeShadow: "0 0 0 3px rgba(236,23,30,0.15)",
      borderRadius: 10,
      colorIcon: "#ffffff",
      cellHoverBg: "rgba(255,255,255,0.1)",
      cellActiveWithRangeBg: "rgba(236,23,30,0.16)",
    },

    Dropdown: {
      colorBgElevated: elevated,
      controlItemBgHover: "rgba(255,255,255,0.07)",
      borderRadiusLG: 14,
      paddingBlock: 8,
    },

    Pagination: {
      borderRadius: 10,
      itemBg: "rgba(255,255,255,0.05)",
      itemActiveBg: "#9B1015",
      colorTextDisabled: "rgba(255,255,255,0.35)",
      itemSize: 34,
    },
    Tooltip: {
      colorBgSpotlight: "#2d2d2d",
      borderRadius: 10,
    },
    Popover: {
      colorBgElevated: "#2d2d2d",
      borderRadiusLG: 14,
    },
    Popconfirm: {
      colorBgElevated: "#2d2d2d",
    },
    Notification: {
      colorBgElevated: elevated,
      borderRadiusLG: 14,
    },
    Message: {
      colorBgElevated: "#2d2d2d",
      borderRadiusLG: 12,
    },
    Tag: {
      borderRadiusSM: 999,
      defaultBg: "rgba(255,255,255,0.07)",
    },
    Segmented: {
      itemSelectedBg: "rgba(236,23,30,0.2)",
      trackBg: "rgba(255,255,255,0.05)",
      borderRadius: 10,
      itemColor: "rgba(255,255,255,0.6)",
      itemHoverColor: "#ffffff",
      itemSelectedColor: "#ffffff",
    },
    Tabs: {
      itemColor: "rgba(255,255,255,0.6)",
      itemSelectedColor: "#ffffff",
      itemHoverColor: "#ffffff",
      inkBarColor: accent,
      horizontalItemGutter: 28,
    },
    Avatar: {
      // antd derives the avatar's fill from colorTextPlaceholder — left at the
      // global value it renders as a pale disc on the dark header.
      colorTextPlaceholder: "rgba(255,255,255,0.12)",
      colorBgContainer: surfaceRaised,
    },
    Upload: {
      colorBorder: line,
      borderRadiusLG: 14,
    },
    Progress: {
      defaultColor: accent,
      remainingColor: "rgba(255,255,255,0.1)",
    },
    Switch: {
      colorPrimary: "#4CAF82",
      colorPrimaryHover: "#5cc294",
    },
    Form: {
      labelColor: "rgba(255,255,255,0.6)",
      itemMarginBottom: 20,
      verticalLabelPadding: "0 0 6px",
    },
    Divider: {
      colorSplit: line,
    },
    Calendar: {
      colorBgContainer: "transparent",
      itemActiveBg: "rgba(236,23,30,0.16)",
      fullBg: "transparent",
      fullPanelBg: "transparent",
    },
  },
};

export default theme;
